import type { ReactNode } from "react";
import { Link } from "react-router-dom";

import { OptimizedImage } from "@/components/ui/optimized-image";
import { ExternalLink } from "@/components/ui/external-link";
import { cn, getUserInitials } from "@/lib/utils";
import { getTeamPath } from "@/utils/team.utils";
import { splitBrCalendarDateYmd } from "@/utils/br-datetime";
import { formatGameDateVisual } from "@/utils/game.utils";
import type { TrainingClass, TrainingSession } from "@/types/training";
import { Icon } from "@/components/ui/icon"
import {
  formatTrainingDate,
  formatTrainingRecurrencePreview,
  formatTrainingScheduleSummary,
  getTrainingClassCoverImage,
  getTrainingSessionCoverImage,
  getTrainingSportLabel,
  splitTrainingEventDate,
  trainingAccessLabel,
  trainingLevelLabel,
  trainingPricingTypeLabel,
  TRAINING_CT_BADGE,
  TRAINING_EVENT_BADGE,
  type TrainingEntityBadgeVariant,
} from "@/utils/training.utils";

export function TrainingEntityBadge({
  variant,
  className,
}: {
  variant: TrainingEntityBadgeVariant;
  className?: string;
}) {
  const badge = variant === "event" ? TRAINING_EVENT_BADGE : TRAINING_CT_BADGE;

  return (
    <span
      className={cn(
        "inline-flex items-center gap-0.5 rounded-full backdrop-blur-sm px-2 py-0.5 text-[9px] font-black uppercase tracking-wider",
        badge.className,
        className)}
    >
      <Icon name={badge.icon} filled />
      {badge.label}
    </span>
  );
}

export function TrainingInfoRow({
  icon,
  label,
  children,
}: {
  icon: string;
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="flex gap-3 rounded-xl border border-border bg-card p-4 shadow-sm">
      <Icon name={icon} aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-0.5">
          {label}
        </p>
        <div className="text-sm font-medium text-foreground leading-snug">{children}</div>
      </div>
    </div>
  );
}

export function TrainingPlaceCard({ location }: { location: string }) {
  const mapsQuery = encodeURIComponent(location.trim());
  const mapsLink = `https://www.google.com/maps/search/?api=1&query=${mapsQuery}`;
  const mapsEmbedUrl = import.meta.env.VITE_GOOGLE_MAPS_API_KEY
    ? `https://www.google.com/maps/embed/v1/place?key=${import.meta.env.VITE_GOOGLE_MAPS_API_KEY}&q=${mapsQuery}`
    : null;

  return (
    <div className="bg-card border border-border shadow-sm rounded-xl p-4 flex flex-col gap-4">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-red-50 dark:bg-red-500/10 flex items-center justify-center text-red-500 shrink-0">
          <Icon name="location_on" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1">
            Local
          </p>
          <h3 className="text-base font-bold text-foreground leading-tight">{location}</h3>
          <ExternalLink
            href={mapsLink}
            className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline mt-2"
          >
            <Icon name="map" />
            Ver no mapa
          </ExternalLink>
        </div>
      </div>

      <div className="w-full h-56 md:h-64 rounded-xl overflow-hidden relative border border-border">
        {mapsEmbedUrl ? (
          <iframe
            title="Local no mapa"
            src={mapsEmbedUrl}
            className="w-full h-full border-0"
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        ) : (
          <ExternalLink
            href={mapsLink}
            className="flex w-full h-full items-center justify-center bg-muted/30 hover:bg-muted/50 transition-colors"
          >
            <Icon name="map" />
            <span className="ml-2 text-sm font-medium text-muted-foreground">
              Clique para ver no mapa
            </span>
          </ExternalLink>
        )}
      </div>
    </div>
  );
}

type OrganizerTeamRef = {
  id: string;
  slug?: string | null;
  name: string;
  photo?: string | null;
};

function HeroDateBadge({ day, month }: { day: number; month: string }) {
  return (
    <>
      <span className="text-3xl sm:text-4xl font-black leading-none">{day}</span>
      <span className="text-[10px] sm:text-xs font-bold uppercase opacity-90 mt-1">
        {month.slice(0, 3)}.
      </span>
    </>
  );
}

function HeroDateStrip({
  badge,
  label,
  title,
}: {
  badge: ReactNode;
  label: string;
  title: string;
}) {
  return (
    <div className="grid sm:grid-cols-[auto_1fr] gap-4 sm:gap-6 items-center bg-card dark:bg-card/95 border-t border-border/60 px-5 py-5 sm:px-8 sm:py-6">
      <div className="flex h-20 w-20 sm:h-24 sm:w-24 flex-col items-center justify-center rounded-2xl bg-primary text-primary-foreground shadow-md shadow-primary/20">
        {badge}
      </div>
      <div>
        <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1">
          {label}
        </p>
        <p className="text-lg sm:text-xl font-bold text-foreground">{title}</p>
      </div>
    </div>
  );
}

function TrainingHeroDateLine({ icon, children }: { icon: string; children: ReactNode }) {
  return (
    <p className="mt-2 text-sm sm:text-base font-medium text-white/90 flex items-center gap-1.5">
      <Icon name={icon} />
      {children}
    </p>
  );
}

function formatTrainingSessionHeroDateLabel(session: TrainingSession) {
  const dateVisual = formatGameDateVisual(session.startsAt, session.endsAt);
  return `${dateVisual.dayName}, ${dateVisual.day} ${dateVisual.month} · ${dateVisual.startTime}${
    dateVisual.endTime ? ` – ${dateVisual.endTime}` : ""
  }`;
}

export function TrainingClassHero({
  trainingClass,
  registrationChip,
}: {
  trainingClass: TrainingClass;
  registrationChip?: { icon: string; label: string; className: string };
}) {
  const heroCover = getTrainingClassCoverImage(trainingClass);
  const sportLabel = getTrainingSportLabel(trainingClass.sport);
  const recurrence =
    formatTrainingRecurrencePreview(
      trainingClass.scheduleDays,
      trainingClass.scheduleTime,
      trainingClass.startsOn,
    ) ??
    formatTrainingScheduleSummary(
      trainingClass.scheduleDays,
      trainingClass.scheduleTime,
      trainingClass.startsOn,
    );

  const calendarDate = splitBrCalendarDateYmd(trainingClass.startsOn);

  return (
    <section className="relative overflow-hidden rounded-3xl border border-border/60 shadow-lg shadow-black/5 dark:shadow-none mb-8 md:mb-10">
      <div className="relative h-[min(52vw,320px)] sm:h-[min(42vw,380px)] md:h-[400px]">
        <OptimizedImage
          src={heroCover}
          alt=""
          priority
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/45 to-black/25" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/50 to-transparent" />

        <div className="absolute top-3 left-3 z-[6] pointer-events-none">
          <TrainingEntityBadge variant="class" className="pointer-events-auto" />
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-8 md:p-10 text-white">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            {registrationChip ? (
              <span
                className={cn(
                  "inline-flex items-center gap-1 rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wide backdrop-blur-sm border",
                  registrationChip.className)}
              >
                <Icon name={registrationChip.icon} />
                {registrationChip.label}
              </span>
            ) : null}
            {sportLabel ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-primary/90 text-primary-foreground px-3 py-1 text-[11px] font-bold uppercase tracking-wide">
                {sportLabel}
              </span>
            ) : null}
            {trainingClass.level ? (
              <span className="inline-flex items-center rounded-full bg-white/10 px-3 py-1 text-[11px] font-semibold backdrop-blur-sm border border-white/15">
                {trainingLevelLabel(trainingClass.level)}
              </span>
            ) : null}
            <span className="inline-flex items-center rounded-full bg-white/10 px-3 py-1 text-[11px] font-semibold backdrop-blur-sm border border-white/15">
              {trainingPricingTypeLabel(trainingClass.pricingType)}
            </span>
          </div>

          <h1 className="text-2xl sm:text-3xl md:text-4xl font-black tracking-tight leading-tight max-w-3xl">
            {trainingClass.name}
          </h1>

          {recurrence ? (
            <TrainingHeroDateLine icon="event_repeat">{recurrence}</TrainingHeroDateLine>
          ) : null}
        </div>
      </div>

      {recurrence ? (
        <HeroDateStrip
          badge={
            calendarDate ? (
              <HeroDateBadge day={calendarDate.day} month={calendarDate.month} />
            ) : (
              <Icon name="event_repeat" />
            )
          }
          label="Horário no CT"
          title={recurrence}
        />
      ) : null}
    </section>
  );
}

export function TrainingSessionHero({
  session,
  registrationChip,
}: {
  session: TrainingSession;
  registrationChip?: { icon: string; label: string; className: string };
}) {
  const heroCover = getTrainingSessionCoverImage(session);
  const sportLabel = getTrainingSportLabel(session.sport);
  const eventDate = splitTrainingEventDate(session.startsAt);

  return (
    <section className="relative overflow-hidden rounded-3xl border border-border/60 shadow-lg shadow-black/5 dark:shadow-none mb-8 md:mb-10">
      <div className="relative h-[min(52vw,320px)] sm:h-[min(42vw,380px)] md:h-[400px]">
        <OptimizedImage
          src={heroCover}
          alt=""
          priority
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/45 to-black/25" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/50 to-transparent" />

        <div className="absolute top-3 left-3 z-[6] pointer-events-none">
          <TrainingEntityBadge variant="event" className="pointer-events-auto" />
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-8 md:p-10 text-white">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            {registrationChip ? (
              <span
                className={cn(
                  "inline-flex items-center gap-1 rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wide backdrop-blur-sm border",
                  registrationChip.className)}
              >
                <Icon name={registrationChip.icon} />
                {registrationChip.label}
              </span>
            ) : null}
            {sportLabel ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-primary/90 text-primary-foreground px-3 py-1 text-[11px] font-bold uppercase tracking-wide">
                {sportLabel}
              </span>
            ) : null}
            {session.level ? (
              <span className="inline-flex items-center rounded-full bg-white/10 px-3 py-1 text-[11px] font-semibold backdrop-blur-sm border border-white/15">
                {session.level}
              </span>
            ) : null}
            <span className="inline-flex items-center rounded-full bg-white/10 px-3 py-1 text-[11px] font-semibold backdrop-blur-sm border border-white/15">
              {trainingAccessLabel(session.accessType, session.pricePerStudent)}
            </span>
          </div>

          <h1 className="text-2xl sm:text-3xl md:text-4xl font-black tracking-tight leading-tight max-w-3xl">
            {session.title}
          </h1>

          <TrainingHeroDateLine icon="event">
            {formatTrainingSessionHeroDateLabel(session)}
          </TrainingHeroDateLine>
        </div>
      </div>

      {eventDate ? (
        <HeroDateStrip
          badge={<HeroDateBadge day={eventDate.day} month={eventDate.month} />}
          label="Data e horário"
          title={formatTrainingDate(session.startsAt)}
        />
      ) : null}
    </section>
  );
}

export function TrainingLocationCard({ session }: { session: TrainingSession }) {
  return <TrainingPlaceCard location={session.location} />;
}

export function OrganizerTeamCard({ team }: { team: OrganizerTeamRef }) {
  return (
    <Link
      to={getTeamPath({ id: team.id, slug: team.slug ?? undefined })}
      className="group flex items-center gap-3 rounded-xl border border-border bg-card-surface p-4 shadow-sm transition-all hover:border-primary/30 hover:bg-primary/5"
    >
      <div className="shrink-0 size-11 rounded-full overflow-hidden ring-2 ring-border bg-muted/30">
        {team.photo?.trim() ? (
          <OptimizedImage src={team.photo} alt="" className="size-11 object-cover" />
        ) : (
          <div className="size-11 flex items-center justify-center text-sm font-bold text-primary bg-primary/10">
            {getUserInitials(team.name)}
          </div>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-0.5">
          Time
        </p>
        <p className="text-sm font-bold text-foreground truncate group-hover:text-primary transition-colors">
          {team.name}
        </p>
      </div>
      <Icon name="chevron_right" />
    </Link>
  );
}

function SummaryQuickStat({
  icon,
  label,
  value,
  suffix,
  className,
}: {
  icon: string;
  label: string;
  value: string;
  suffix?: string;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "rounded-xl border border-border/80 bg-muted/30 dark:border-border p-3",
        className)}
    >
      <Icon name={icon} />
      <p className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground mt-2">
        {label}
      </p>
      <p className="mt-1 flex flex-wrap items-baseline gap-x-1 leading-tight">
        <span className="text-lg font-black text-foreground tabular-nums">{value}</span>
        {suffix ? (
          <span className="text-xs font-semibold text-muted-foreground whitespace-nowrap">
            {suffix}
          </span>
        ) : null}
      </p>
    </div>
  );
}

export type TrainingEnrollmentSummaryCardProps = {
  subtitle: string;
  priceLabel: string;
  /** Rótulo do stat de preço (padrão: Valor). CT usa Mensalidade. */
  priceStatLabel?: string;
  /** Sufixo exibido ao lado do valor (ex.: / mês). */
  priceSuffix?: string;
  spotsLabel: string;
  filledSpots?: number;
  maxSpots?: number;
  /** No desktop (lg+), oculta o stat de preço — valor fica só na barra mobile. */
  hidePriceOnDesktop?: boolean;
};

export function TrainingEnrollmentSummaryCard({
  subtitle,
  priceLabel,
  priceStatLabel = "Valor",
  priceSuffix,
  spotsLabel,
  filledSpots = 0,
  maxSpots = 0,
  hidePriceOnDesktop = false,
}: TrainingEnrollmentSummaryCardProps) {
  const occupancyPct =
    maxSpots > 0 ? Math.min(100, Math.round((filledSpots / maxSpots) * 100)) : null;

  return (
    <div className="rounded-2xl border border-card-border bg-card p-5 shadow-sm space-y-4 w-full">
      <div>
        <h2 className="text-sm font-bold uppercase tracking-wide text-muted-foreground">Resumo</h2>
        <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
      </div>
      <div
        className={cn(
          "grid gap-3",
          hidePriceOnDesktop ? "grid-cols-1 lg:grid-cols-1" : "grid-cols-2")}
      >
        <SummaryQuickStat
          icon="payments"
          label={priceStatLabel}
          value={priceLabel}
          suffix={priceSuffix}
          className={hidePriceOnDesktop ? "lg:hidden" : undefined}
        />
        <SummaryQuickStat icon="groups" label="Vagas" value={spotsLabel} />
      </div>
      {occupancyPct != null ? (
        <div className="space-y-2 pt-1">
          <div className="flex items-center justify-between text-xs">
            <span className="font-semibold text-muted-foreground">Ocupação</span>
            <span className="font-bold tabular-nums text-foreground">{occupancyPct}%</span>
          </div>
          <div className="h-2 rounded-full bg-muted overflow-hidden">
            <div
              className="h-full rounded-full bg-primary transition-all duration-500"
              style={{ width: `${occupancyPct}%` }}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}
